import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useEffect, useState } from "react";
import {
  Shield,
  Star,
  Check,
  X,
  Trash2,
  Download,
  ArrowLeft,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  deleteTestimonial,
  downloadCsv,
  getAllTestimonials,
  getEventFeedback,
  getFeatureFeedback,
  setTestimonialApproval,
  type Testimonial,
  type EventFeedback,
  type FeatureFeedback,
} from "@/lib/testimonials";
import { MemberFeedbackModeration } from "@/components/admin/MemberFeedbackModeration";

export const Route = createFileRoute("/admin-testimonials")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Testimonials & Feedback — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: AdminTestimonials,
});

type Tab =
  | "homepage"
  | "stats"
  | "member-feedback"
  | "testimonials"
  | "events"
  | "features";

function AdminTestimonials() {
  const [tab, setTab] = useState<Tab>("homepage");
  const [tList, setTList] = useState<Testimonial[]>([]);
  const [eList, setEList] = useState<EventFeedback[]>([]);
  const [fList, setFList] = useState<FeatureFeedback[]>([]);

  const refresh = () => {
    setTList(getAllTestimonials());
    setEList(getEventFeedback());
    setFList(getFeatureFeedback());
  };
  useEffect(() => {
    refresh();
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <Link
        to="/admin"
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Admin
      </Link>

      <header className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/15 text-[var(--gold-deep)]">
          <Shield className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-3xl text-foreground">
            Testimonials & Feedback
          </h1>
          <p className="text-sm text-muted-foreground">
            Approve member testimonials and review event & feature feedback.
          </p>
        </div>
      </header>

      <div className="mb-6 flex flex-wrap gap-1 rounded-2xl border border-gold/20 bg-card p-1.5">
        {(
          [
            ["homepage", "Homepage Carousel"],
            ["stats", "Homepage Stats"],
            ["member-feedback", "Member Feedback"],
            ["testimonials", "Member Submissions"],
            ["events", "Event Feedback"],
            ["features", "Feature Feedback"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
              tab === id
                ? "bg-gold text-white shadow-sm shadow-gold/30"
                : "text-foreground/70 hover:bg-gold/10"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "homepage" && <HomepageCarouselTab />}
      {tab === "stats" && <HomepageStatsTab />}
      {tab === "member-feedback" && <MemberFeedbackModeration />}
      {tab === "testimonials" && (
        <TestimonialsTab list={tList} onChange={refresh} />
      )}
      {tab === "events" && <EventTab list={eList} />}
      {tab === "features" && <FeatureTab list={fList} />}
    </div>
  );
}

function Stars({ n }: { n: number }) {
  return (
    <span className="inline-flex text-[var(--gold-deep)]">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i < n ? "fill-current" : "opacity-30"}`}
        />
      ))}
    </span>
  );
}

function TestimonialsTab({
  list,
  onChange,
}: {
  list: Testimonial[];
  onChange: () => void;
}) {
  const pending = list.filter((t) => !t.approved);
  const approved = list.filter((t) => t.approved);

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <button
          onClick={() => downloadCsv("testimonials.csv", list)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-gold/30 bg-card px-3 py-2 text-sm text-foreground hover:bg-gold/10"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <Section title={`Pending review (${pending.length})`}>
        {pending.length === 0 ? (
          <Empty>No testimonials waiting for review.</Empty>
        ) : (
          pending.map((t) => (
            <Row key={t.id} t={t} onChange={onChange} />
          ))
        )}
      </Section>

      <Section title={`Approved (${approved.length})`}>
        {approved.map((t) => (
          <Row key={t.id} t={t} onChange={onChange} />
        ))}
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h2 className="mb-3 font-display text-xl text-foreground">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-border/50 bg-cream/30 p-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function Row({ t, onChange }: { t: Testimonial; onChange: () => void }) {
  const locked = t.source === "seed";
  return (
    <article className="rounded-2xl border border-border/50 bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-foreground">{t.name}</span>
          <Stars n={t.rating} />
          {t.feature && (
            <span className="rounded-full border border-gold/30 bg-cream/40 px-2 py-0.5">
              {t.feature}
            </span>
          )}
          <time>{new Date(t.date).toLocaleDateString()}</time>
          {locked && (
            <span className="rounded-full bg-muted px-2 py-0.5">seed</span>
          )}
        </div>
        <div className="flex gap-2">
          {!locked && t.approved && (
            <button
              onClick={() => {
                setTestimonialApproval(t.id, false);
                onChange();
              }}
              className="inline-flex items-center gap-1 rounded-lg border border-border/60 px-2 py-1 text-xs hover:bg-muted"
            >
              <X className="h-3 w-3" /> Unapprove
            </button>
          )}
          {!locked && !t.approved && (
            <button
              onClick={() => {
                setTestimonialApproval(t.id, true);
                onChange();
              }}
              className="inline-flex items-center gap-1 rounded-lg bg-gold px-2 py-1 text-xs text-white hover:bg-gold/90"
            >
              <Check className="h-3 w-3" /> Approve
            </button>
          )}
          {!locked && (
            <button
              onClick={() => {
                if (confirm("Delete this testimonial?")) {
                  deleteTestimonial(t.id);
                  onChange();
                }
              }}
              className="inline-flex items-center gap-1 rounded-lg border border-destructive/30 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-3 w-3" /> Delete
            </button>
          )}
        </div>
      </div>
      <p className="mt-3 text-sm text-foreground">"{t.quote}"</p>
      {(t.helpedMost || t.mostUsed || t.wantNext) && (
        <dl className="mt-3 grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
          {t.helpedMost && (
            <div>
              <dt className="font-semibold text-foreground">Helped most</dt>
              <dd>{t.helpedMost}</dd>
            </div>
          )}
          {t.mostUsed && (
            <div>
              <dt className="font-semibold text-foreground">Most used</dt>
              <dd>{t.mostUsed}</dd>
            </div>
          )}
          {t.wantNext && (
            <div>
              <dt className="font-semibold text-foreground">Wants next</dt>
              <dd>{t.wantNext}</dd>
            </div>
          )}
        </dl>
      )}
    </article>
  );
}

function EventTab({ list }: { list: EventFeedback[] }) {
  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          onClick={() => downloadCsv("event-feedback.csv", list)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-gold/30 bg-card px-3 py-2 text-sm text-foreground hover:bg-gold/10"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>
      {list.length === 0 ? (
        <Empty>No event feedback yet.</Empty>
      ) : (
        <ul className="space-y-3">
          {list.map((f) => (
            <li
              key={f.id}
              className="rounded-2xl border border-border/50 bg-card p-4"
            >
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">
                  {f.eventTitle}
                </span>
                <div className="flex items-center gap-3">
                  <Stars n={f.rating} />
                  <time>{new Date(f.date).toLocaleString()}</time>
                </div>
              </div>
              {f.comment && (
                <p className="mt-2 text-sm text-foreground">{f.comment}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FeatureTab({ list }: { list: FeatureFeedback[] }) {
  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          onClick={() => downloadCsv("feature-feedback.csv", list)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-gold/30 bg-card px-3 py-2 text-sm text-foreground hover:bg-gold/10"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>
      {list.length === 0 ? (
        <Empty>No feature feedback yet.</Empty>
      ) : (
        <ul className="space-y-3">
          {list.map((f) => (
            <li
              key={f.id}
              className="rounded-2xl border border-border/50 bg-card p-4"
            >
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-foreground">
                    {f.feature}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 ${
                      f.helpful
                        ? "bg-gold/15 text-foreground"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    {f.helpful ? "👍 Helpful" : "👎 Needs Improvement"}
                  </span>
                </div>
                <time>{new Date(f.date).toLocaleString()}</time>
              </div>
              {f.comment && (
                <p className="mt-2 text-sm text-foreground">{f.comment}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================
// HOMEPAGE CAROUSEL — DB-backed (public.testimonials)
// ============================================================

type DbTestimonial = {
  id: string;
  quote: string;
  author_name: string;
  author_role: string | null;
  rating: number;
  avatar_url: string | null;
  source: string;
  is_published: boolean;
  is_featured: boolean;
  sort_order: number;
  created_at: string;
};

const blankTestimonial = (): Partial<DbTestimonial> => ({
  quote: "",
  author_name: "",
  author_role: "",
  rating: 5,
  avatar_url: "",
  source: "Community",
  is_published: true,
  is_featured: false,
  sort_order: 0,
});

function HomepageCarouselTab() {
  const [rows, setRows] = useState<DbTestimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Partial<DbTestimonial>>(blankTestimonial());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("testimonials")
      .select("*")
      .order("is_published", { ascending: false })
      .order("is_featured", { ascending: false })
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) setError(error.message);
    else setRows((data ?? []) as DbTestimonial[]);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const save = async () => {
    setError(null);
    if (!draft.quote?.trim() || !draft.author_name?.trim()) {
      setError("Quote and author name are required.");
      return;
    }
    const payload = {
      quote: draft.quote!.trim(),
      author_name: draft.author_name!.trim(),
      author_role: draft.author_role?.trim() || null,
      rating: Number(draft.rating ?? 5),
      avatar_url: draft.avatar_url?.trim() || null,
      source: draft.source?.trim() || "Community",
      is_published: !!draft.is_published,
      is_featured: !!draft.is_featured,
      sort_order: Number(draft.sort_order ?? 0),
    };
    const { error } = editingId
      ? await supabase.from("testimonials").update(payload).eq("id", editingId)
      : await supabase.from("testimonials").insert(payload);
    if (error) {
      setError(error.message);
      return;
    }
    setDraft(blankTestimonial());
    setEditingId(null);
    void load();
  };

  const togglePublish = async (row: DbTestimonial) => {
    await supabase
      .from("testimonials")
      .update({ is_published: !row.is_published })
      .eq("id", row.id);
    void load();
  };

  const toggleFeature = async (row: DbTestimonial) => {
    await supabase
      .from("testimonials")
      .update({ is_featured: !row.is_featured })
      .eq("id", row.id);
    void load();
  };

  const swapOrder = async (a: DbTestimonial, b: DbTestimonial) => {
    await supabase
      .from("testimonials")
      .update({ sort_order: b.sort_order })
      .eq("id", a.id);
    await supabase
      .from("testimonials")
      .update({ sort_order: a.sort_order })
      .eq("id", b.id);
    void load();
  };

  const remove = async (row: DbTestimonial) => {
    if (!confirm(`Delete testimonial from ${row.author_name}?`)) return;
    await supabase.from("testimonials").delete().eq("id", row.id);
    void load();
  };

  const startEdit = (row: DbTestimonial) => {
    setEditingId(row.id);
    setDraft(row);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gold/30 bg-card p-5">
        <h2 className="font-display text-lg text-foreground">
          {editingId ? "Edit testimonial" : "Add a testimonial"}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Only items marked Published appear in the homepage carousel.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="sm:col-span-2 text-sm">
            <span className="mb-1 block text-xs uppercase tracking-wider text-foreground/70">
              Quote *
            </span>
            <textarea
              value={draft.quote ?? ""}
              onChange={(e) => setDraft({ ...draft, quote: e.target.value })}
              rows={3}
              className="w-full rounded-xl border border-border bg-background p-2 text-sm"
              placeholder="This page helped me understand trauma in a way no one else could."
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs uppercase tracking-wider text-foreground/70">
              Author name *
            </span>
            <input
              value={draft.author_name ?? ""}
              onChange={(e) => setDraft({ ...draft, author_name: e.target.value })}
              className="w-full rounded-xl border border-border bg-background p-2 text-sm"
              placeholder="Community Member"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs uppercase tracking-wider text-foreground/70">
              Author role / label
            </span>
            <input
              value={draft.author_role ?? ""}
              onChange={(e) => setDraft({ ...draft, author_role: e.target.value })}
              className="w-full rounded-xl border border-border bg-background p-2 text-sm"
              placeholder="Beta tester · UK"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs uppercase tracking-wider text-foreground/70">
              Rating (1–5)
            </span>
            <input
              type="number"
              min={1}
              max={5}
              value={draft.rating ?? 5}
              onChange={(e) => setDraft({ ...draft, rating: Number(e.target.value) })}
              className="w-full rounded-xl border border-border bg-background p-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs uppercase tracking-wider text-foreground/70">
              Source
            </span>
            <input
              value={draft.source ?? ""}
              onChange={(e) => setDraft({ ...draft, source: e.target.value })}
              className="w-full rounded-xl border border-border bg-background p-2 text-sm"
              placeholder="Community · Trustpilot · Instagram"
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block text-xs uppercase tracking-wider text-foreground/70">
              Avatar URL (optional)
            </span>
            <input
              value={draft.avatar_url ?? ""}
              onChange={(e) => setDraft({ ...draft, avatar_url: e.target.value })}
              className="w-full rounded-xl border border-border bg-background p-2 text-sm"
              placeholder="https://…"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs uppercase tracking-wider text-foreground/70">
              Sort order
            </span>
            <input
              type="number"
              value={draft.sort_order ?? 0}
              onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) })}
              className="w-full rounded-xl border border-border bg-background p-2 text-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!draft.is_published}
              onChange={(e) => setDraft({ ...draft, is_published: e.target.checked })}
            />
            <span>Published (visible on homepage)</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!draft.is_featured}
              onChange={(e) => setDraft({ ...draft, is_featured: e.target.checked })}
            />
            <span>⭐ Featured (pin to front of carousel)</span>
          </label>
        </div>
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <div className="mt-4 flex gap-2">
          <button
            onClick={save}
            className="rounded-xl bg-gold px-4 py-2 text-sm font-semibold text-white hover:bg-gold/90"
          >
            {editingId ? "Save changes" : "Add testimonial"}
          </button>
          {editingId && (
            <button
              onClick={() => {
                setEditingId(null);
                setDraft(blankTestimonial());
              }}
              className="rounded-xl border border-border px-4 py-2 text-sm text-foreground hover:bg-muted"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-display text-xl text-foreground">
          All testimonials ({rows.length})
        </h2>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <Empty>No testimonials yet. Add one above.</Empty>
        ) : (
          <ul className="space-y-3">
            {rows.map((r, idx) => (
              <li
                key={r.id}
                className={`rounded-2xl border p-4 ${
                  r.is_featured && r.is_published
                    ? "border-gold/60 bg-gradient-to-br from-gold-light/40 to-card ring-1 ring-gold/40"
                    : r.is_published
                      ? "border-gold/30 bg-card"
                      : "border-border/50 bg-muted/30 opacity-75"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-semibold text-foreground">
                      {r.author_name}
                    </span>
                    {r.author_role && <span>· {r.author_role}</span>}
                    <Stars n={r.rating} />
                    <span className="rounded-full bg-foreground/5 px-2 py-0.5">
                      {r.source}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 font-semibold ${
                        r.is_published
                          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                          : "bg-muted text-foreground/70"
                      }`}
                    >
                      {r.is_published ? "● Published" : "○ Hidden"}
                    </span>
                    {r.is_featured && (
                      <span className="rounded-full bg-gold/30 px-2 py-0.5 font-semibold text-foreground">
                        ⭐ Featured
                      </span>
                    )}
                    <span>sort: {r.sort_order}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => idx > 0 && swapOrder(r, rows[idx - 1])}
                      disabled={idx === 0}
                      className="rounded-lg border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-40"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => idx < rows.length - 1 && swapOrder(r, rows[idx + 1])}
                      disabled={idx === rows.length - 1}
                      className="rounded-lg border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-40"
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => toggleFeature(r)}
                      className={`rounded-lg border px-2 py-1 text-xs ${
                        r.is_featured
                          ? "border-gold/50 bg-gold/15 text-foreground hover:bg-gold/25"
                          : "border-border hover:bg-muted"
                      }`}
                    >
                      {r.is_featured ? "★ Unfeature" : "☆ Feature"}
                    </button>
                    <button
                      onClick={() => togglePublish(r)}
                      className="rounded-lg border border-border px-2 py-1 text-xs hover:bg-muted"
                    >
                      {r.is_published ? "Hide" : "Publish"}
                    </button>
                    <button
                      onClick={() => startEdit(r)}
                      className="rounded-lg border border-border px-2 py-1 text-xs hover:bg-muted"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(r)}
                      className="rounded-lg border border-destructive/30 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                <p className="mt-3 text-sm text-foreground">"{r.quote}"</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================================
// HOMEPAGE STATS — DB-backed (public.homepage_stats)
// ============================================================

type DbStat = {
  id: string;
  emoji: string | null;
  label: string;
  value: string;
  sort_order: number;
  is_published: boolean;
};

function HomepageStatsTab() {
  const [rows, setRows] = useState<DbStat[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("homepage_stats")
      .select("*")
      .order("sort_order", { ascending: true });
    setRows((data ?? []) as DbStat[]);
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, []);

  const update = async (id: string, patch: Partial<DbStat>) => {
    await supabase.from("homepage_stats").update(patch).eq("id", id);
    void load();
  };
  const remove = async (id: string) => {
    if (!confirm("Delete this stat tile?")) return;
    await supabase.from("homepage_stats").delete().eq("id", id);
    void load();
  };
  const add = async () => {
    const { error } = await supabase.from("homepage_stats").insert({
      emoji: "✨",
      label: "New stat",
      value: "—",
      sort_order: (rows[rows.length - 1]?.sort_order ?? 0) + 10,
      is_published: true,
    });
    if (error) alert(error.message);
    void load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          The 6 trust tiles shown below the homepage testimonial carousel. Edit in
          place; toggle Published to show/hide.
        </p>
        <button
          onClick={add}
          className="rounded-xl bg-gold px-3 py-2 text-sm font-semibold text-white hover:bg-gold/90"
        >
          + Add tile
        </button>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gold/20 bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-start text-xs uppercase tracking-wider text-foreground/60">
              <tr>
                <th className="px-3 py-2">Emoji</th>
                <th className="px-3 py-2">Value</th>
                <th className="px-3 py-2">Label</th>
                <th className="px-3 py-2 w-20">Sort</th>
                <th className="px-3 py-2 w-28">Published</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/50">
                  <td className="px-3 py-2">
                    <input
                      defaultValue={r.emoji ?? ""}
                      onBlur={(e) =>
                        e.target.value !== (r.emoji ?? "") &&
                        update(r.id, { emoji: e.target.value || null })
                      }
                      className="w-14 rounded-lg border border-border bg-background p-1 text-center text-lg"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      defaultValue={r.value}
                      onBlur={(e) =>
                        e.target.value !== r.value && update(r.id, { value: e.target.value })
                      }
                      className="w-full rounded-lg border border-border bg-background p-1"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      defaultValue={r.label}
                      onBlur={(e) =>
                        e.target.value !== r.label && update(r.id, { label: e.target.value })
                      }
                      className="w-full rounded-lg border border-border bg-background p-1"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      defaultValue={r.sort_order}
                      onBlur={(e) =>
                        Number(e.target.value) !== r.sort_order &&
                        update(r.id, { sort_order: Number(e.target.value) })
                      }
                      className="w-16 rounded-lg border border-border bg-background p-1"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={r.is_published}
                      onChange={(e) =>
                        update(r.id, { is_published: e.target.checked })
                      }
                    />
                  </td>
                  <td className="px-3 py-2 text-end">
                    <button
                      onClick={() => remove(r.id)}
                      className="rounded-lg border border-destructive/30 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
